import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  getOwnDeletionSchedule,
  getUserProfile,
  updateUserProfile,
  userCancelDeletion,
  userScheduleDeletion,
} from "~/lib/functions/user";
import type { UserProfileForm } from "~/lib/schemas/user-auth";
import type { AccountDeletionRefusal } from "~/lib/shared/user-deletion";

export const userQueryKeys = {
  all: ["user"] as const,
  profile: (userId: string) =>
    [...userQueryKeys.all, "profile", userId] as const,
  deletionSchedules: () => [...userQueryKeys.all, "deletion-schedule"] as const,
  deletionSchedule: (userId: string) =>
    [...userQueryKeys.deletionSchedules(), userId] as const,
};

export const userProfileQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: userQueryKeys.profile(userId),
    queryFn: () => getUserProfile(),
    staleTime: Infinity,
  });

export const userDeletionScheduleQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: userQueryKeys.deletionSchedule(userId),
    queryFn: () => getOwnDeletionSchedule(),
  });

export const useUpdateUserProfile = (options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UserProfileForm) => updateUserProfile({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      options?.onError?.(error);
    },
  });
};

export interface DeletionRequestResult {
  success: boolean;
  error?: string;
  unsupported?: boolean;
  /**
   * True when this call changed nothing but a deletion recorded earlier is
   * still pending, so the account really will be deleted.
   */
  existingDeletionPending?: boolean;
  /** ISO date of that still-pending deletion, when it is known. */
  existingDeletionAt?: string;
  /** Why the request was refused; nothing was scheduled. */
  refusal?: AccountDeletionRefusal;
}

interface DeletionMutationOptions {
  onSuccess?: (result: DeletionRequestResult) => void;
  onError?: (error: Error) => void;
}

/**
 * Scheduling and cancelling share one shape. Both refresh only the deletion
 * schedule: refetching the profile would reset the form under unsaved edits.
 * Identity comes from the authenticated session on the server, so the caller
 * has nothing to pass.
 */
const useDeletionMutation = (
  request: () => Promise<DeletionRequestResult>,
  options?: DeletionMutationOptions,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (result) => {
      options?.onSuccess?.(result);
    },
    onError: (error) => {
      console.error("Account deletion request error:", error);
      options?.onError?.(error as Error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.deletionSchedules(),
      });
    },
  });
};

export const useScheduleUserDeletion = (options?: DeletionMutationOptions) =>
  useDeletionMutation(() => userScheduleDeletion(), options);

export const useCancelUserDeletion = (options?: DeletionMutationOptions) =>
  useDeletionMutation(() => userCancelDeletion(), options);
